import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { buildEmailPreviewDocument } from "../../utils/emailTemplateHtml";

type EmailTemplatePreviewProps = {
  html: string;
  minHeight?: number;
  className?: string;
};

export default function EmailTemplatePreview({
  html,
  minHeight = 120,
  className = "",
}: EmailTemplatePreviewProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timeoutIdsRef = useRef<number[]>([]);

  const previewDocument = useMemo(
    () => buildEmailPreviewDocument(html),
    [html]
  );

  const clearResizeTasks = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    timeoutIdsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    timeoutIdsRef.current = [];
  }, []);

  const updateFrameHeight = useCallback(() => {
    const iframe = frameRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) {
      return;
    }

    const nextHeight = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.body?.offsetHeight ?? 0,
      doc.documentElement?.scrollHeight ?? 0,
      doc.documentElement?.offsetHeight ?? 0,
      minHeight
    );

    const nextHeightPx = `${Math.ceil(nextHeight)}px`;
    if (iframe.style.height !== nextHeightPx) {
      iframe.style.height = nextHeightPx;
    }
  }, [minHeight]);

  const scheduleResize = useCallback(() => {
    clearResizeTasks();

    animationFrameRef.current = window.requestAnimationFrame(() => {
      updateFrameHeight();
      animationFrameRef.current = null;
    });

    timeoutIdsRef.current = [80, 200, 500, 1000].map((delay) =>
      window.setTimeout(updateFrameHeight, delay)
    );
  }, [clearResizeTasks, updateFrameHeight]);

  const handleLoad = useCallback(() => {
    const iframe = frameRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) {
      return;
    }

    scheduleResize();

    Array.from(doc.images).forEach((image) => {
      if (!image.complete) {
        image.addEventListener("load", scheduleResize, { once: true });
        image.addEventListener("error", scheduleResize, { once: true });
      }
    });

    if (doc.fonts?.ready) {
      doc.fonts.ready.then(() => {
        scheduleResize();
      }).catch(() => undefined);
    }
  }, [scheduleResize]);

  useEffect(() => {
    clearResizeTasks();
  }, [previewDocument, clearResizeTasks]);

  useEffect(() => {
    return () => {
      clearResizeTasks();
    };
  }, [clearResizeTasks]);

  return (
    <div
      className={`rounded border border-gray-200 bg-gray-50 overflow-hidden ${className}`.trim()}
    >
      <iframe
        ref={frameRef}
        title="Email template preview"
        srcDoc={previewDocument}
        sandbox="allow-same-origin"
        className="block w-full border-0 bg-white"
        style={{ minHeight: `${minHeight}px` }}
        onLoad={handleLoad}
      />
    </div>
  );
}
